
import type { FC } from 'react';
import { cn } from '@/lib/utils';

export type MascotVariant = 
  | 'welcome' 
  | 'welcome-alt' 
  | 'not-found' 
  | 'error' 
  | 'empty' 
  | 'notifications' 
  | 'award' 
  | 'default';

export type MascotSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export type MascotAnimation = 'float' | 'none';

interface MascotProps {
  variant?: MascotVariant;
  size?: MascotSize;
  className?: string;
  alt?: string;
  animate?: MascotAnimation;
}

const MASCOT_PATHS: Record<MascotVariant, string> = {
  'welcome': '/characters/Welcome.svg',
  'welcome-alt': '/characters/Welcome (Alt).svg',
  'not-found': '/characters/Page Not Found.svg',
  'error': '/characters/Something Went Wrong.svg',
  'empty': '/characters/Empty States.svg',
  'notifications': '/characters/Notifications.svg',
  'award': '/characters/Award.svg',
  'default': '/characters/Default.svg',
};

const SIZE_CLASSES: Record<MascotSize, string> = {
  // Inline scale — sits next to a line of text (collapsed Today widget strips)
  // rather than carrying an empty state on its own.
  xs: 'w-8 h-8',
  sm: 'w-24 h-24 sm:w-32 sm:h-32',
  md: 'w-32 h-32 sm:w-40 sm:h-40',
  lg: 'w-40 h-40 sm:w-48 sm:h-48 md:w-56 md:h-56',
  xl: 'w-48 h-48 sm:w-56 sm:h-56 md:w-64 md:h-64',
};

const VARIANT_LABELS: Record<MascotVariant, string> = {
  'welcome': 'Welcome mascot',
  'welcome-alt': 'Welcome mascot',
  'not-found': 'Page not found mascot',
  'error': 'Error mascot',
  'empty': 'Empty state mascot',
  'notifications': 'Notifications mascot',
  'award': 'Award mascot',
  'default': 'Scrambl mascot',
};

export const Mascot: FC<MascotProps> = ({
  variant = 'default',
  size = 'md',
  className,
  alt,
  animate = 'float',
}) => {
  const imagePath = MASCOT_PATHS[variant];
  const sizeClass = SIZE_CLASSES[size];
  const altText = alt || VARIANT_LABELS[variant];

  return (
    <div
      className={cn(
        'flex items-center justify-center',
        'mascot-entrance',
        animate === 'float' && 'mascot-float',
        className
      )}
    >
      <img
        src={imagePath}
        alt={altText}
        className={cn(
          'object-contain',
          sizeClass
        )}
      />
    </div>
  );
};
